/**
 * CIPAA Public Candidature Page
 *
 * Public page for employees to register as candidates.
 * Supports two modes:
 * - Token mode: /candidatura/:token (single use)
 * - AccessKey mode: /candidatura/eleicao/:key (public link, unlimited uses)
 *
 * No authentication required.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  HowToReg as CandidateIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Business as CompanyIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { cipaaService } from '../../services/cipaaService';
import { formatDate } from '../../types/cipaa';

type PageState = 'loading' | 'valid' | 'invalid' | 'form' | 'submitting' | 'success' | 'error';
type CandidatureMode = 'token' | 'accessKey';

interface ElectionInfo {
  id: number;
  name: string;
  companyName: string;
  startDate: string;
  endDate: string;
}

interface EmployeeInfo {
  name: string;
  cpf: string;
  department?: string;
  occupation?: string;
}

interface CipaaPublicCandidaturePageProps {
  mode?: CandidatureMode;
}

const CipaaPublicCandidaturePage: React.FC<CipaaPublicCandidaturePageProps> = ({ mode = 'token' }) => {
  const { token, key } = useParams<{ token?: string; key?: string }>();

  // Determine the actual mode based on props or URL params
  const actualMode: CandidatureMode = mode === 'accessKey' || key ? 'accessKey' : 'token';
  const identifier = actualMode === 'accessKey' ? key : token;

  const [pageState, setPageState] = useState<PageState>('loading');
  const [electionInfo, setElectionInfo] = useState<ElectionInfo | null>(null);
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Form state
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');

  useEffect(() => {
    if (!identifier) return;

    const validateAccess = async () => {
      setPageState('loading');
      try {
        if (actualMode === 'token') {
          // Token mode: validate the single-use token
          const result = await cipaaService.validateToken(identifier);

          if (result.valid && result.election) {
            setElectionInfo({
              id: result.election.id,
              name: result.election.name,
              companyName: result.election.company?.tradeName || result.election.company?.legalName || 'Empresa',
              startDate: result.election.startDate,
              endDate: result.election.endDate,
            });
            setPageState('form');
          } else {
            setErrorMessage(result.message ?? 'Token inválido');
            setPageState('invalid');
          }
        } else {
          // AccessKey mode: get election by key
          const election = await cipaaService.getElectionByKey(identifier);

          if (!election.canRegisterCandidates) {
            setErrorMessage('Período de inscrição não está aberto');
            setPageState('invalid');
            return;
          }

          setElectionInfo({
            id: election.id,
            name: election.name,
            companyName: election.company?.tradeName || election.company?.legalName || 'Empresa',
            startDate: election.startDate,
            endDate: election.endDate,
          });
          setPageState('form');
        }
      } catch (err: any) {
        setErrorMessage(err.message ?? 'Erro ao validar acesso');
        setPageState('invalid');
      }
    };

    validateAccess();
  }, [identifier, actualMode]);

  const formatCpfInput = (value: string) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');

    // Apply mask: 000.000.000-00
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpfInput(e.target.value);
    setCpf(formatted);
    setCpfError('');
  };

  const validateCpfFormat = (cpfValue: string): boolean => {
    const digits = cpfValue.replace(/\D/g, '');
    if (digits.length !== 11) {
      setCpfError('CPF deve ter 11 dígitos');
      return false;
    }
    // Basic CPF validation (all same digits)
    if (/^(\d)\1+$/.test(digits)) {
      setCpfError('CPF inválido');
      return false;
    }
    return true;
  };

  const handleValidateCpf = async () => {
    if (!validateCpfFormat(cpf)) return;

    setPageState('loading');
    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      let employee;

      if (actualMode === 'token') {
        // Token mode: validate CPF using the token, which carries the election
        employee = await cipaaService.validateCpf(identifier!, cleanCpf);
      } else {
        // AccessKey mode: validate CPF using accessKey
        employee = await cipaaService.validateCpfByKey(identifier!, cleanCpf);
      }

      setEmployeeInfo({
        name: employee.name,
        cpf: cpf,
        department: typeof employee.department === 'object' ? employee.department?.name : employee.department,
        occupation: typeof employee.occupation === 'object' ? employee.occupation?.name : employee.occupation,
      });
      setPageState('valid');
    } catch (err: any) {
      setCpfError(err.message ?? 'CPF não encontrado na empresa');
      setPageState('form');
    }
  };

  const handleSubmit = async () => {
    setPageState('submitting');
    try {
      const cleanCpf = cpf.replace(/\D/g, '');

      if (actualMode === 'token') {
        // Token mode: register using token (consumes the token)
        await cipaaService.registerCandidature(identifier!, cleanCpf);
      } else {
        // AccessKey mode: register using accessKey (doesn't consume anything)
        await cipaaService.registerCandidatureByKey(identifier!, cleanCpf);
      }

      setPageState('success');
    } catch (err: any) {
      setErrorMessage(err.message ?? 'Erro ao registrar candidatura');
      setPageState('error');
    }
  };

  const handleBack = () => {
    setEmployeeInfo(null);
    setPageState('form');
  };

  // Loading state
  if (pageState === 'loading' || pageState === 'submitting') {
    return (
      <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">
            {pageState === 'loading' ? 'Validando...' : 'Registrando candidatura...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Invalid token/key
  if (pageState === 'invalid') {
    return (
      <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {actualMode === 'token' ? 'Token Inválido' : 'Acesso Inválido'}
            </Typography>
            <Typography color="text.secondary">
              {errorMessage}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Success
  if (pageState === 'success') {
    return (
      <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Candidatura Registrada!
            </Typography>
            <Typography color="text.secondary" paragraph>
              Sua inscrição como candidato foi realizada com sucesso.
            </Typography>
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Próximo passo:</strong> Aguarde a aprovação da sua candidatura pela comissão eleitoral.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Error
  if (pageState === 'error') {
    return (
      <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Erro no Registro
            </Typography>
            <Typography color="text.secondary" paragraph>
              {errorMessage}
            </Typography>
            <Button variant="outlined" onClick={() => setPageState('form')}>
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Confirmation state
  if (pageState === 'valid' && employeeInfo) {
    return (
      <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CandidateIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" gutterBottom>
                Confirmar Candidatura
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Verifique seus dados antes de confirmar a inscrição.
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Eleição
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {electionInfo?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {electionInfo?.companyName}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Candidato
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {employeeInfo.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CPF: {employeeInfo.cpf}
              </Typography>
              {employeeInfo.occupation && (
                <Typography variant="body2" color="text.secondary">
                  Cargo: {employeeInfo.occupation}
                </Typography>
              )}
              {employeeInfo.department && (
                <Typography variant="body2" color="text.secondary">
                  Setor: {employeeInfo.department}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleBack}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
              >
                Confirmar Inscrição
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Form state (default)
  return (
    <Box sx={{ maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 }, py: 8 }}>
      <Card>
        <CardContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CandidateIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" gutterBottom>
              Inscrição de Candidatura
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Eleição CIPAA
            </Typography>
          </Box>

          {electionInfo && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CompanyIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  {electionInfo.companyName}
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                {electionInfo.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Período: {formatDate(electionInfo.startDate)} a {formatDate(electionInfo.endDate)}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" paragraph>
            Informe seu CPF para verificar sua elegibilidade e registrar sua candidatura.
          </Typography>

          <TextField
            fullWidth
            label="CPF"
            value={cpf}
            onChange={handleCpfChange}
            error={!!cpfError}
            helperText={cpfError || 'Digite seu CPF'}
            placeholder="000.000.000-00"
            inputProps={{ maxLength: 14 }}
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleValidateCpf}
            disabled={cpf.replace(/\D/g, '').length !== 11}
          >
            Verificar e Continuar
          </Button>
        </CardContent>
      </Card>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Comissão Interna de Prevenção de Acidentes e Assédio
      </Typography>
    </Box>
  );
};

export default CipaaPublicCandidaturePage;
